import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { InventoryModel } from 'client/data/models/inventory';
import { CoreResearchEntities } from 'client/data/models/core-research';
import { generateSrpLinkNewMake, generateSrpLinkUsedMake } from 'site-modules/shared/utils/srp-link-constructor';
import { TYPE_FILTER } from 'site-modules/make/constants/make';
import { separatePubStates } from 'site-modules/make/utils/make';
import { getMakeIncentivesLink } from 'site-modules/shared/utils/incentives-link-constructor';
import { ArrowLink } from 'site-modules/shared/components/arrow-link/arrow-link';
import { MakeLink } from 'site-modules/make/components/make-info/make-link';

import { FiltersList } from './filters-list';
import { ModelsList } from './models-list';

function ModelsViewUI({
  submodelsList = null,
  makeSlug,
  makeName,
  newInventoryCount = 0,
  pubStates = {},
  ads11412Recipe = 'ctrl',
}) {
  const [activeFilter, setActiveFilter] = useState(TYPE_FILTER.ALL);

  if (!submodelsList || !submodelsList.length) {
    return null;
  }

  const { preProdList, newUsedList } = separatePubStates(submodelsList);

  const hasNewInvLink = newInventoryCount > 0;
  const hasUsedInvLink = pubStates && (pubStates.USED || pubStates.NEW_USED);

  const isAds11412Ctrl = ads11412Recipe === 'ctrl';

  return (
    <Fragment>
      {newUsedList && !!newUsedList.length && (
        <Fragment>
          {isAds11412Ctrl && (
            <Fragment>
              <div data-tracking-parent="make-index-summary">
                {hasNewInvLink && (
                  <Fragment>
                    <ArrowLink
                      to={generateSrpLinkNewMake(makeSlug)}
                      className="mt-1_5"
                      data-tracking-id="view_search_result"
                    >
                      See all new {makeName} vehicles for sale
                    </ArrowLink>
                    <ArrowLink
                      to={`/${makeSlug}-lease-deals/`}
                      className="mt-1_5"
                      data-tracking-id="view_search_result"
                    >
                      Lease a new {makeName}
                    </ArrowLink>
                  </Fragment>
                )}
                <ArrowLink
                  to={getMakeIncentivesLink(makeSlug)}
                  className="mt-1_5"
                  data-tracking-id="link_select_tool_select"
                  data-tracking-value="incentives"
                >
                  {makeName} tax credits, rebates and incentives
                </ArrowLink>
              </div>
              <hr className="my-1" />
              <FiltersList
                activeFilter={activeFilter}
                submodelsList={submodelsList}
                setActiveFilter={setActiveFilter}
                className="mb-1_5"
              />
            </Fragment>
          )}
          {!isAds11412Ctrl && (
            <Fragment>
              <h2 className="heading-3">Browse {makeName} Vehicles</h2>
              <div className="d-flex align-items-center mb-1_5">
                <span className="fw-bold me-1 mb-0_25">Filter by:</span>
                <FiltersList
                  activeFilter={activeFilter}
                  submodelsList={submodelsList}
                  setActiveFilter={setActiveFilter}
                />
              </div>
            </Fragment>
          )}
          <ModelsList activeFilter={activeFilter} submodelsList={newUsedList} ads11412Recipe={ads11412Recipe} />
          {hasUsedInvLink && (
            <Fragment>
              {isAds11412Ctrl ? (
                <ArrowLink
                  to={generateSrpLinkUsedMake(makeSlug)}
                  className="mb-1 mt-0_5"
                  data-tracking-id="view_search_result"
                  data-test="used-inventory-link"
                >
                  Shop used {makeName} inventory
                </ArrowLink>
              ) : (
                <MakeLink
                  label={`Shop used ${makeName} inventory`}
                  iconClassName="icon-price-tag3"
                  to={generateSrpLinkUsedMake(makeSlug)}
                  data-tracking-id="view_search_result"
                  data-test="used-inventory-link"
                  className="bg-blue-50 rounded-12 mt-1_5 mb-1_5"
                  iconContainerClassName="text-blue-50 bg-white"
                  labelClassName="text-white fw-bold"
                />
              )}
            </Fragment>
          )}
        </Fragment>
      )}
      <ModelsList
        activeFilter={activeFilter}
        submodelsList={preProdList}
        heading="Upcoming models"
        ads11412Recipe={ads11412Recipe}
      />
    </Fragment>
  );
}

ModelsViewUI.propTypes = {
  makeSlug: PropTypes.string.isRequired,
  makeName: PropTypes.string.isRequired,
  submodelsList: PropTypes.arrayOf(CoreResearchEntities.VehicleSubmodel),
  pubStates: PropTypes.shape({}),
  newInventoryCount: PropTypes.number,
  ads11412Recipe: PropTypes.string,
};

export const ModelsView = connectToModel(ModelsViewUI, {
  // Inventory filters for new make inventory are set in make-definition.jsx
  newInventoryCount: bindToPath('searchResults', InventoryModel, searchResults =>
    get(searchResults, 'inventories.totalNumber', 0)
  ),
});
